import React, { useRef, useState, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import useDebounce from '../../../../utils/useDebounce'
import TextInput from '../../../../shared/Inputs/TextInput/TextInput'
import SelectInput from '../../../../shared/Inputs/SelectInput/SelectInput'
import FileInput from '../../../../shared/Inputs/FileInput/FileInput'
import { UPLOADS_ENDPOINT } from '../../../../server-parameters'
import { yearsAgo } from '../../../../utils/helpers'
import {
  validateName,
  stringDigitsLength,
  validateString,
  validateDate,
  smartValidateString,
} from '../../../../validate/validators'
import { Country, Lang } from '../../../../types/common-types'
import { State } from '../../../../state-actions-reducers'
import warningImage from '../../../../images/warning.svg'
import DatePicker from '../../../../shared/Inputs/DatePicker/DatePicker'
import { checkPhoneEmail } from '../../../../hooks/useCheckPhoneEmailCustomHook'
import styles from '../../FiscalScreenOne.module.scss'
import RussianForm from '../KzInstance/Citizenships/RussianForm'
import KyrgyzstanForm from '../KzInstance/Citizenships/KyrgyzstanForm'
import RomanianForm from '../KzInstance/Citizenships/RomanianForm'
import ChinaForm from '../KzInstance/Citizenships/ChinaForm'
import KazakhstanForm from '../KzInstance/Citizenships/KazakhstanForm'
import BelarusForm from '../KzInstance/Citizenships/BelarusForm'
import UzbekistanForm from '../KzInstance/Citizenships/UzbekistanForm'
import VietnamForm from '../KzInstance/Citizenships/VietnamForm'
import AddressInputs from '../../AddressInputs/AddressInputs'
import {
  passportTypeKz,
  passportTypeRfForKz,
  passportTypeRom,
  DocumentTypeValuesUdvAbroad,
  DocumentTypeValuesTypePersonalities,
  DocumentTypeValuesTypePassport,
} from '../../../../content/passportType'
import InnExist from '../../../../components/IdentityExist/InnExist'

interface Props {
  lang: Lang
  accountForm: any
  citizenshipCountriesList: Country[]
  addressCountries: Country[]
  regionCityRegistered: any
  regionCityFactual: any
  regionCityResidence: any
}

type CitizenshipFormProps = { lang: any; accountForm: any }

const CITIZENSHIP_PASSPORT_TYPE_MAP: Record<string, any> = {
  'Казахстан': passportTypeKz,
  'Румыния': passportTypeRom,
}

const CITIZENSHIP_FORM_MAP: Record<string, React.ComponentType<CitizenshipFormProps>> = {
  'Российская Федерация': RussianForm,
  'Китай': ChinaForm,
  'Казахстан': KazakhstanForm,
  'Кыргызстан': KyrgyzstanForm,
  'Румыния': RomanianForm,
  'Беларусь': BelarusForm,
  'Бразилия': BelarusForm,
  'Италия': BelarusForm,
  'Австралия': UzbekistanForm,
  'Азербайджан': UzbekistanForm,
  'Армения': UzbekistanForm,
  'Соединенное Королевство': UzbekistanForm,
  'Германия': UzbekistanForm,
  'Испания': UzbekistanForm,
  'Сирийская Арабская Республика': UzbekistanForm,
  'Соединенные Штаты': UzbekistanForm,
  'Таджикистан': UzbekistanForm,
  'Узбекистан': UzbekistanForm,
  'Франция': UzbekistanForm,
  'Швейцария': UzbekistanForm,
  'Болгария': VietnamForm,
  'Израиль': VietnamForm,
  'Исландия': VietnamForm,
  'Молдова, Республика': VietnamForm,
  'Нидерланды': VietnamForm,
  'Норвегия': VietnamForm,
  'Швеция': VietnamForm,
  'Южная Африка': VietnamForm,
  'Вьетнам': VietnamForm,
  'Грузия': VietnamForm,
  'Индонезия': VietnamForm,
  'Индия': VietnamForm,
  'Южная Корея': VietnamForm,
  'Финляндия': VietnamForm,
}

/* REACT COMPONENT */
function IpScreenOne({
  lang,
  accountForm,
  citizenshipCountriesList,
  addressCountries,
  regionCityRegistered,
  regionCityFactual,
  regionCityResidence
}: Props) {
  const { watch, setValue, getValues, formState: { errors } } = useFormContext<State>()
  const state = watch()

  const getCitizenshipFormat = (citizenship: string) => {
    return citizenshipCountriesList.find((country: Country) => {
      return country.value === citizenship
    })?.label?.rus
  }
  const selectedCitizenship = useMemo(() => {
    return getCitizenshipFormat(state.citizenship)
  }, [state.citizenship])

  const [focus, setFocus] = useState('')

  const debouncedPhone = useDebounce(state.mobilePhoneNumbers[0], 1500)
  const debouncedMail = useDebounce(state.emails[0], 1500)

  async function phoneEmailMessage() {
    try {
      let response = await checkPhoneEmail(state, debouncedPhone, debouncedMail)
      if (response.message !== 'Invalid token') {
        setValue('isPhoneOk', response.isPhoneOk, { shouldDirty: true })
        setValue('isEmailOk', response.isEmailOk, { shouldDirty: true })
      } else {
        setValue('serverError', true, { shouldDirty: true });
        alert(accountForm.serverError[lang]);
      }
    } catch (err) {
      setValue('serverError', true, { shouldDirty: true });
      alert(accountForm.serverError[lang]);
    }
  }
  useEffect(() => {
    if (debouncedPhone && debouncedPhone.length > 2) {
      phoneEmailMessage()
    } else {
      setValue('isPhoneOk', true, { shouldDirty: true })
    }
  }, [debouncedPhone])
  useEffect(() => {
    if (debouncedMail) {
      phoneEmailMessage()
    } else {
      setValue('isEmailOk', true, { shouldDirty: true })
    }
  }, [debouncedMail])

  useEffect(() => {
    setValue('F_FiscalType', '1', { shouldDirty: true })
  }, [state.countryRegistration])

  useEffect(() => {
    if (state.passportType.length === 0)
      setValue('passportType', selectedCitizenship === 'Казахстан'
            ? String(passportTypeKz[0].value)
            : selectedCitizenship === 'Румыния'
              ? String(DocumentTypeValuesTypePersonalities[0].value)
              : selectedCitizenship === 'Российская Федерация' ||
                selectedCitizenship === 'Кыргызстан'
                ? String(DocumentTypeValuesTypePassport[0].value)
                : String(DocumentTypeValuesTypePassport[0].value), { shouldDirty: true })
  }, [selectedCitizenship, state.F_FiscalType])

  const CitizenshipForm = selectedCitizenship ? CITIZENSHIP_FORM_MAP[selectedCitizenship] ?? null : null

  return (
    <>
      <div>
        <div className="form-group">
          <div className="row">
            <div className="col-2 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.F_INN}
                onChange={F_INN => {
                  setValue('F_INN', F_INN.replace(/\D/gim, '').substr(0, 12), { shouldDirty: true })
                }}
                // onChange={F_INN =>
                //   setValue('F_INN', F_INN, { shouldDirty: true })
                // }
                value={state.F_INN || ''}
                required
                valid={
                  stringDigitsLength(state.F_INN, 12, false) &&
                  !errors.F_INN
                }
                mask={'999999999999'}
              />
            </div>
            <InnExist
              accountForm={accountForm}
              lang={lang}
              
              show={!state.isIdentityOk}
            />
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_INN_Image}
                
                onChange={F_INN_Image => {
                  setValue('F_INN_Image', [...(getValues('F_INN_Image')||[]), F_INN_Image], { shouldDirty: true })
                }}
                value={state.F_INN_Image[state.F_INN_Image.length - 1]}
                valid={!errors.F_INN_Image}
                documentType="INN"
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-2 col-xs-12">
              {/* F_OGRN */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.F_OGRNIP}
                onChange={F_OGRN => {
                  setValue('F_OGRN', F_OGRN.replace(/\D/gim, '').substr(0, 15), { shouldDirty: true })
                }}
                // onChange={F_OGRN =>
                //   setValue('F_OGRN', F_OGRN, { shouldDirty: true })
                // }
                value={state.F_OGRN || ''}
                valid={
                  stringDigitsLength(state.F_OGRN, 15, false) &&
                  !errors.F_OGRN
                }
                mask={'999999999999999'}
              />
            </div>
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_OGRNIP_Image}
                
                onChange={F_OGRN_Image => {
                  setValue('F_OGRN_Image', [...(getValues('F_OGRN_Image')||[]), F_OGRN_Image], { shouldDirty: true })
                }}
                value={state.F_OGRN_Image[state.F_OGRN_Image.length - 1]}
                valid={!errors.F_OGRN_Image}
                documentType="OGRN"
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-2 col-xs-12">
              <DatePicker
                lang={lang}
                accountForm={accountForm}
                accountFormKey="registeredDate"
                onChange={data => {
                  setValue('registeredDate', data, { shouldDirty: true })
                }}
                value={state.registeredDate}
                focus={focus === 'registeredDate' || undefined}
                onBlur={() => setFocus('')}
                valid={!errors.registeredDate}
                required
                min={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 67)
                  )
                }
                max={new Date()}
              />
            </div>
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_EGRIP_Image}
                
                onChange={F_EGRUL_Image => {
                  setValue('F_EGRUL_Image', [...(getValues('F_EGRUL_Image')||[]), F_EGRUL_Image], { shouldDirty: true })
                }}
                value={state.F_EGRUL_Image[state.F_EGRUL_Image.length - 1]}
                valid={!errors.F_EGRUL_Image}
                documentType="EGRYUL"
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={state.noLeaseAgreement}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_Rent_Contract_Image}
                
                onChange={F_Rent_Contract_Image => {
                  setValue('F_Rent_Contract_Image', [...(getValues('F_Rent_Contract_Image')||[]), F_Rent_Contract_Image], { shouldDirty: true })
                }}
                value={
                  state.F_Rent_Contract_Image[
                  state.F_Rent_Contract_Image.length - 1
                  ]
                }
                valid={!errors.F_Rent_Contract_Image}
                documentType="Lease"
                imageErrors={state.imageError}
              />
            </div>
            <div className={styles.checkInputBox + ' ' + 'col-2 col-xs-12'}>
              <input
                id="hideContractInput"
                name="hideContractInput"
                type="checkBox"
                onChange={() => {
                  setValue('noLeaseAgreement', !state.noLeaseAgreement, { shouldDirty: true })
                  setValue('F_Rent_Contract_Image', [], { shouldDirty: true })
                }}
                checked={state.noLeaseAgreement}
              />
              <label htmlFor="hideContractInput">
                {accountForm.noRentContractBtnDesc[lang]}
              </label>
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12">
              <h3>{accountForm.stepOneIdentification[lang]}</h3>
            </div>
            <div className="col-4 col-xs-12">
              {/* LAST NAME */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.lastName}
                onChange={headLastname =>
                  setValue('headLastname', headLastname, { shouldDirty: true })
                }
                value={state.headLastname || ''}
                required
                valid={
                  state.headLastname.length > 0
                    ? smartValidateString(state.headLastname) &&
                    !errors.headLastname
                    : !errors.headLastname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* FIRST NAME */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.firstName}
                onChange={headFirstname =>
                  setValue('headFirstname', headFirstname, { shouldDirty: true })
                }
                value={state.headFirstname || ''}
                required
                valid={
                  state.headFirstname.length > 0
                    ? smartValidateString(state.headFirstname) &&
                    !errors.headFirstname
                    : !errors.headFirstname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* PATRONYMIC */}
              <TextInput
                disabled={!state.agreed || state.noPatronymic}
                lang={lang}
                content={accountForm.patronymic}
                onChange={headPatronymic =>
                  setValue('headPatronymic', headPatronymic, { shouldDirty: true })
                }
                checkLabel={accountForm.noPatronymic[lang]}
                checkValue={state.noPatronymic}
                checkChange={() => {
                  setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true })
                  setValue('headPatronymic', '', { shouldDirty: true })
                }}
                required
                value={state.headPatronymic || ''}
                valid={
                  state.headPatronymic.length > 0
                    ? smartValidateString(state.headPatronymic) &&
                    !errors.headPatronymic
                    : !errors.headPatronymic
                }
              />
            </div>
          </div>
          <div className="row">
            <div className="col-4 col-xs-12">
              {/* CITIZENSHIP */}
              <SelectInput
                testName={'citizenship'}
                testErrorName={'error-citizenship'}
                noOptionsMessage={accountForm.citizenshipInput.placeholder}
                lang={lang}
                disabled={!state.agreed}
                content={accountForm.citizenshipInput}
                options={citizenshipCountriesList}
                onChange={citizenship => {
                  setValue('citizenship', citizenship, { shouldDirty: true })
                  setValue('passportType', getCitizenshipFormat(citizenship) === 'Казахстан'
                        ? String(passportTypeKz[0].value)
                        : getCitizenshipFormat(citizenship) === 'Румыния'
                          ? String(DocumentTypeValuesTypePersonalities[0].value)
                          : getCitizenshipFormat(citizenship) === 'Российская Федерация' ||
                            getCitizenshipFormat(citizenship) === 'Кыргызстан'
                            ? String(DocumentTypeValuesTypePassport[0].value)
                            : String(DocumentTypeValuesTypePassport[0].value), { shouldDirty: true })
                }}
                valid={!errors.citizenship}
                value={state.citizenship}
                required
                prefix="Citizenships"
              />
            </div>
            <div className="col-3 col-xs-12">
              {/* BIRTH DATE */}
              <DatePicker
                disabled={!state.agreed}
                lang={lang}
                accountForm={accountForm}
                accountFormKey="birthDate"
                onChange={birthDate =>
                  setValue('birthDate', birthDate, { shouldDirty: true })
                }
                value={state.birthDate}
                valid={
                  !errors.birthDate &&
                  validateDate({
                    current: state.birthDate,
                    min: yearsAgo(130),
                    max: yearsAgo(14),
                  })
                }
                curr={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 18)
                  )
                }
                min={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 85)
                  )
                }
                max={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 18)
                  )
                }
                required
              />
            </div>
            <div className="col-5 col-xs-12">
              {/* BIRTH PLACE */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.birthPlace}
                onChange={birthPlace => {
                  setValue('birthPlace', birthPlace, { shouldDirty: true })
                }}
                value={state.birthPlace}
                required
                valid={
                  state.birthPlace.length > 0
                    ? smartValidateString(state.birthPlace) &&
                    !errors.birthPlace
                    : !errors.birthPlace
                }
                onBlur={() => setFocus('')}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-3 col-xs-12">
              <SelectInput
                testName={'passportType'}
                testErrorName={'error-passportType'}
                lang={lang}
                noOptionsMessage={accountForm.passportTypeInput.placeholder}
                options={
                  selectedCitizenship
                    ? (CITIZENSHIP_PASSPORT_TYPE_MAP[selectedCitizenship] ?? DocumentTypeValuesTypePassport)
                    : DocumentTypeValuesTypePassport
                }
                content={accountForm.passportTypeInput}
                onChange={data => {
                  setValue('passportType', data, { shouldDirty: true })
                }}
                value={state.passportType}
                valid={
                  !errors.passportType &&
                  validateString(state.passportType, 300, true)
                }
                required
              />
            </div>
            {CitizenshipForm && <CitizenshipForm lang={lang} accountForm={accountForm} />}
            <div className="col-12 col-xs-12">
              <p className={styles.warningMessage}>
                <img src={warningImage} alt="warningImage"></img>
                {accountForm.Fiscal_Warning_Scan_Directors_Passport_KZT[lang]}
              </p>
            </div>
          </div>
        </div>
        <AddressInputs
          lang={lang}
          
          accountForm={accountForm}
          addressCountries={addressCountries}
          regionCityRegistered={regionCityRegistered}
          regionCityFactual={regionCityFactual}
          regionCityResidence={regionCityResidence}
        />
      </div>
    </>
  )
}

export default IpScreenOne
